import api from './api';

export const DEFAULT_MAIN_MENU = [
  { label: 'Home', path: '/', public: true },
  { label: 'Dashboard', path: '/dashboard', auth: true },
  { label: 'Billing', path: '/billing', auth: true },
  { label: 'Admin', path: '/admin', admin: true },
];

export const DEFAULT_ADMIN_MENU = [
  { path: '/admin', label: 'Overview', icon: '📊', exact: true },
  { path: '/admin/users', label: 'Users', icon: '👥' },
  { path: '/admin/plans', label: 'Plans', icon: '💳' },
  { path: '/admin/content', label: 'Site Content', icon: '📝' },
  { path: '/admin/api-providers', label: 'API Providers', icon: '🔌' },
  { path: '/admin/payments', label: 'Payments', icon: '💰' },
  { path: '/admin/settings', label: 'Settings', icon: '⚙️' },
];

export const DEFAULT_ADMIN_QUICK_ACTIONS = [
  { label: 'Manage Users', path: '/admin/users' },
  { label: 'Manage Plans', path: '/admin/plans' },
  { label: 'Edit Content', path: '/admin/content' },
  { label: 'API Providers', path: '/admin/api-providers' },
  { label: 'View Payments', path: '/admin/payments' },
  { label: 'Settings', path: '/admin/settings' },
];

const DEFAULT_DESCRIPTION = 'VoxifyAI - Text-to-Speech Web Application with Voice Cloning';

const cloneItems = (items) => items.map((item) => ({ ...item }));

const normalizePath = (path, fallback) => {
  if (typeof path !== 'string' || !path.trim()) return fallback;
  const normalized = path.trim();
  return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

const normalizeMainMenu = (items) => {
  if (!Array.isArray(items) || items.length === 0) return cloneItems(DEFAULT_MAIN_MENU);

  const normalized = items
    .filter((item) => item && typeof item.label === 'string' && typeof item.path === 'string')
    .map((item) => ({
      label: item.label.trim(),
      path: normalizePath(item.path, '/'),
      public: item.public,
      auth: item.auth,
      admin: item.admin,
    }))
    .filter((item) => item.label && item.path);

  return normalized.length > 0 ? normalized : cloneItems(DEFAULT_MAIN_MENU);
};

const normalizeAdminMenu = (items) => {
  if (!Array.isArray(items) || items.length === 0) return cloneItems(DEFAULT_ADMIN_MENU);

  const normalized = items
    .filter((item) => item && typeof item.label === 'string' && typeof item.path === 'string')
    .map((item) => ({
      label: item.label.trim(),
      path: normalizePath(item.path, '/admin'),
      icon: typeof item.icon === 'string' ? item.icon : '•',
      exact: Boolean(item.exact),
    }))
    .filter((item) => item.label && item.path);

  return normalized.length > 0 ? normalized : cloneItems(DEFAULT_ADMIN_MENU);
};

const normalizeQuickActions = (items) => {
  if (!Array.isArray(items) || items.length === 0) return cloneItems(DEFAULT_ADMIN_QUICK_ACTIONS);

  const normalized = items
    .filter((item) => item && typeof item.label === 'string' && typeof item.path === 'string')
    .map((item) => ({
      label: item.label.trim(),
      path: normalizePath(item.path, '/admin'),
    }))
    .filter((item) => item.label && item.path);

  return normalized.length > 0 ? normalized : cloneItems(DEFAULT_ADMIN_QUICK_ACTIONS);
};

export const getDefaultUISettings = () => ({
  siteName: 'VoxifyAI',
  siteDescription: DEFAULT_DESCRIPTION,
  logoUrl: '',
  faviconUrl: '/vite.svg',
  seoTitle: 'VoxifyAI - Text to Speech',
  seoDescription: DEFAULT_DESCRIPTION,
  seoKeywords: '',
  seoOgImage: '',
  mainMenu: cloneItems(DEFAULT_MAIN_MENU),
  adminMenu: cloneItems(DEFAULT_ADMIN_MENU),
  adminQuickActions: cloneItems(DEFAULT_ADMIN_QUICK_ACTIONS),
});

export const normalizeUISettings = (contentItem) => {
  const defaults = getDefaultUISettings();
  const extraData =
    contentItem?.extra_data && typeof contentItem.extra_data === 'object'
      ? contentItem.extra_data
      : {};

  return {
    ...defaults,
    siteName: contentItem?.title || defaults.siteName,
    siteDescription: contentItem?.content || defaults.siteDescription,
    logoUrl: typeof extraData.logo_url === 'string' ? extraData.logo_url : defaults.logoUrl,
    faviconUrl:
      typeof extraData.favicon_url === 'string' && extraData.favicon_url.trim()
        ? extraData.favicon_url
        : defaults.faviconUrl,
    seoTitle: contentItem?.meta_title || defaults.seoTitle,
    seoDescription: contentItem?.meta_description || contentItem?.content || defaults.seoDescription,
    seoKeywords: contentItem?.meta_keywords || defaults.seoKeywords,
    seoOgImage: typeof extraData.seo_og_image === 'string' ? extraData.seo_og_image : '',
    mainMenu: normalizeMainMenu(extraData.menu_items),
    adminMenu: normalizeAdminMenu(extraData.admin_menu_items),
    adminQuickActions: normalizeQuickActions(extraData.admin_quick_actions),
  };
};

export async function getPublicUISettings() {
  try {
    const response = await api.get('/content/ui_settings');
    return normalizeUISettings(response.data);
  } catch (error) {
    if (error.response?.status !== 404) {
      console.error('Failed to load UI settings:', error);
    }
    return getDefaultUISettings();
  }
}
